import { TooltipComponentOption } from 'echarts';
import { merge } from 'lodash';
import { useMemo } from 'react';
import {
  COMMON_LABELS_AUTO_COLLAPSE_THRESHOLD,
  COMMON_LABELS_POPULATED_MAX_NEARBY_SERIES,
  DEFAULT_MAX_NEARBY_SERIES,
  DEFAULT_TOOLTIP_CONFIG,
  TooltipConfig,
  TooltipLink,
} from '@/components';
import {
  AppMetadataProps,
  ContextLinkDefinition,
  GetSeriesById,
  TIME_SERIES_TOOLTIP_MODE_DATAPOINTS_LIMIT,
  TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT,
  TimeSeriesTotals,
  TooltipMode,
} from '@/core';
import { useQueryTruncation } from '@/plugin-system';
import { useInterpolateContextLinkFn } from '@/plugin-system/runtime/context-links';

const EMPTY_TOOLTIP_LINKS: TooltipLink[] = [];

/**
 * Empty ECharts tooltip option that allows shared crosshair to work with custom React tooltip.
 */
export function getTooltipOption(): TooltipComponentOption {
  return {
    show: true,
    showContent: false,
    trigger: 'axis', // needed for shared axisPointer to work
  };
}

export function isOptimizedTooltipMode(totalSeries: number, totalDatapoints: number) {
  return (
    totalSeries > TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT || totalDatapoints > TIME_SERIES_TOOLTIP_MODE_DATAPOINTS_LIMIT
  );
}

/**
 * Tooltip customization util, passed to TimeChartBase / custom React tooltip using TimeSeriesTooltipProvider.
 */
export function useTooltipConfig({
  tooltipMode,
  totalSeries,
  totalDatapoints,
  totalValuePerTimestamp,
  sort,
  maxNearbySeries = DEFAULT_MAX_NEARBY_SERIES,
  enableTooltipPreciseValues,
  enablePinning,
  showEndTime,
  showTotal,
  excludeEmptyResults,
  contextLinks,
  getSeriesById,
  wrapSeriesName,
  rightYAxisIndex,
  hasLogScale,
  isStacked,
  tooltipContextMenuProps,
  tooltipContextMenuData,
  appMetadata,
  panelKey,
  commonLabels,
}: Omit<TooltipConfig, 'mode' | 'links'> & {
  totalSeries: number;
  totalDatapoints: number;
  totalValuePerTimestamp?: TimeSeriesTotals;
  tooltipMode?: TooltipMode;
  contextLinks?: ContextLinkDefinition[];
  isStacked?: boolean;
  appMetadata?: AppMetadataProps;
  panelKey?: string;
}): TooltipConfig {
  const tooltipLinks = useTooltipLinksFromContextLinks(contextLinks, getSeriesById);

  const autoTooltipMode = useMemo(() => {
    return isOptimizedTooltipMode(totalSeries, totalDatapoints) ? 'single' : tooltipMode;
  }, [totalSeries, totalDatapoints, tooltipMode]);

  // Increase maxNearbySeries for stacked charts to show total more often.
  let adjustedMaxNearbySeries = isStacked ? TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT : maxNearbySeries;
  if (autoTooltipMode === 'single') {
    adjustedMaxNearbySeries = 1;
  } else if (!isStacked && commonLabels && Object.keys(commonLabels).length > 0) {
    // Adjust for common labels section vertical space (skip for stacked charts since total is shown in tooltip footer)
    adjustedMaxNearbySeries = Math.min(adjustedMaxNearbySeries, COMMON_LABELS_POPULATED_MAX_NEARBY_SERIES);
  }

  const showTotalInTooltipFooter = useShouldShowTotal({ showTotal, totalSeries });

  // Check whether to expand common labels section by default
  const numCommonLabels = commonLabels ? Object.keys(commonLabels).length : 0;
  const expandCommonLabels = numCommonLabels > 0 && numCommonLabels < COMMON_LABELS_AUTO_COLLAPSE_THRESHOLD;

  const tooltipConfig = useMemo(() => {
    const mergedConfig = merge({}, DEFAULT_TOOLTIP_CONFIG, {
      mode: autoTooltipMode,
      sort,
      maxNearbySeries: adjustedMaxNearbySeries,
      enableTooltipPreciseValues,
      enablePinning,
      showEndTime,
      showTotal: showTotalInTooltipFooter,
      totalValuePerTimestamp,
      excludeEmptyResults,
      links: tooltipLinks,
      wrapSeriesName,
      rightYAxisIndex,
      getSeriesConfig: (seriesId: string) => {
        if (getSeriesById) {
          const series = getSeriesById(seriesId);
          return series?.config;
        }
      },
      getSeriesById,
      hasLogScale,
      isStacked,
      tooltipContextMenuProps,
      tooltipContextMenuData,
      appMetadata,
      panelKey,
      commonLabels: autoTooltipMode === 'single' ? undefined : commonLabels,
      expandCommonLabels,
    });
    return mergedConfig;
  }, [
    autoTooltipMode,
    enablePinning,
    enableTooltipPreciseValues,
    excludeEmptyResults,
    getSeriesById,
    hasLogScale,
    isStacked,
    rightYAxisIndex,
    showEndTime,
    sort,
    tooltipLinks,
    wrapSeriesName,
    showTotalInTooltipFooter,
    totalValuePerTimestamp,
    adjustedMaxNearbySeries,
    tooltipContextMenuProps,
    tooltipContextMenuData,
    appMetadata,
    panelKey,
    commonLabels,
    expandCommonLabels,
  ]);

  return tooltipConfig;
}

/**
 * Mapping of context link spec to tooltip links used in SeriesTooltipActions.
 */
function useTooltipLinksFromContextLinks(links: ContextLinkDefinition[] | undefined, getSeriesById: GetSeriesById) {
  const interpolateContextLink = useInterpolateContextLinkFn();

  const tooltipLinks: TooltipLink[] = useMemo(() => {
    if (!links) {
      return EMPTY_TOOLTIP_LINKS;
    }
    return links?.map((link) => ({
      title: link.spec.title,
      buildUrl: (event) => {
        // If there is no seriesId, we can't interpolate the URL
        if (!getSeriesById) {
          return link.spec.url;
        }
        // Look up the original series by id so we can get its metadata
        const series = getSeriesById(event.seriesId);
        // Return the interpolated URL
        return interpolateContextLink(link.spec.url, {
          name: series?.name,
          labels: series?.labels,
          value: event.y,
          timestamp: event.x,
        });
      },
    }));
  }, [links, getSeriesById, interpolateContextLink]);
  return tooltipLinks;
}

/**
 * Determine whether to show total in the tooltip footer based on spec and vf config context.
 * When data is incomplete due to truncated queries, we should not show a misleading total.
 */
export function useShouldShowTotal({
  showTotal = false,
  totalSeries,
}: {
  showTotal?: boolean;
  totalSeries: number;
}): boolean {
  const queryLimits = useQueryTruncation()?.queryLimits;
  if (!showTotal) return false;
  if (queryLimits && queryLimits.enabled) {
    if (queryLimits.seriesLimit !== undefined && totalSeries >= queryLimits.seriesLimit) {
      return false;
    }
  }
  return true;
}
